import { Button, Modal, Spin } from 'antd';
import QRCode from 'qrcode';
import React, { useRef, useState } from 'react';
import Icon from '../../../components/icon/Icon';
import { errorWithUserMessage, success } from '../../../helpers/NotificationHelper';
import { MatchTableLinksVm } from '../../../utils/api';
import './tableLinks.css';
import { getTableLinks } from './tableLinksCache';

type Layout = 'report' | 'log';

type Phase =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'message'; message: string }
    | {
          kind: 'ready';
          clockUrl: string;
          scoreboardUrl: string;
          clockQr?: string;
          scoreboardQr?: string;
          validFrom: string;
          validUntil: string;
      };

interface TableLinksActionProps {
    matchId: number;
    layout: Layout;
}

const formatLocal = (value: string | undefined, label: string): string => {
    if (!value) {
        errorWithUserMessage(`Table links did not include ${label}.`);
        return 'not provided';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        errorWithUserMessage(`Table links returned an unreadable ${label}.`);
        return value;
    }
    return date.toLocaleString();
};

const drawQr = async (url: string, label: string): Promise<string | undefined> => {
    try {
        return await QRCode.toDataURL(url, { margin: 1, width: 180, errorCorrectionLevel: 'M' });
    } catch {
        errorWithUserMessage(`Could not draw the ${label} QR code.`);
        return undefined;
    }
};

const copyLink = async (url: string): Promise<void> => {
    try {
        if (!navigator.clipboard || !navigator.clipboard.writeText) {
            errorWithUserMessage('Could not copy the link.');
            return;
        }
        await navigator.clipboard.writeText(url);
        success('Link copied.');
    } catch {
        errorWithUserMessage('Could not copy the link.');
    }
};

const TableLinksAction: React.FC<TableLinksActionProps> = ({ matchId, layout }) => {
    const [open, setOpen] = useState(false);
    const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
    const requestId = useRef(0);

    const load = async (): Promise<void> => {
        const id = requestId.current + 1;
        requestId.current = id;
        setPhase({ kind: 'loading' });
        let result: MatchTableLinksVm;
        try {
            result = await getTableLinks(matchId);
        } catch (error) {
            if (id !== requestId.current) return;
            errorWithUserMessage(error);
            setPhase({ kind: 'message', message: 'Could not load table links.' });
            return;
        }
        if (id !== requestId.current) return;

        if (result.status !== 'ok' || !result.clock?.url || !result.scoreboard?.url) {
            const message = result.message || 'Could not load table links.';
            errorWithUserMessage(message);
            setPhase({ kind: 'message', message });
            return;
        }

        const validFrom = formatLocal(result.validFrom, 'valid from');
        const validUntil = formatLocal(result.validUntil, 'valid until');
        const [clockQr, scoreboardQr] = await Promise.all([
            drawQr(result.clock.url, 'clock'),
            drawQr(result.scoreboard.url, 'scoreboard'),
        ]);
        if (id !== requestId.current) return;
        setPhase({
            kind: 'ready',
            clockUrl: result.clock.url,
            scoreboardUrl: result.scoreboard.url,
            clockQr,
            scoreboardQr,
            validFrom,
            validUntil,
        });
    };

    const openPanel = (): void => {
        setOpen(true);
        void load();
    };

    const entry = (
        <div className="c-extended-menu__item u-flex u-flex-column u-align-items-center u-justify-content-center">
            <Icon name="icon-stopwatch" />
            <span>Table links</span>
        </div>
    );

    return (
        <>
            {layout === 'report' ? (
                <div
                    className="c-extended-menu__list u-flex u-flex-row u-flex-nowrap"
                    onClick={openPanel}
                    role="button"
                >
                    {entry}
                </div>
            ) : (
                <div
                    className="c-extended-menu__item u-flex u-flex-column u-align-items-center u-justify-content-center"
                    onClick={openPanel}
                    role="button"
                >
                    <Icon name="icon-stopwatch" />
                    <span>Table links</span>
                </div>
            )}
            <Modal
                title="Table links"
                visible={open}
                onCancel={(): void => setOpen(false)}
                footer={null}
                width={720}
                destroyOnClose
            >
                {phase.kind === 'loading' || phase.kind === 'idle' ? (
                    <div className="table-links__loading">
                        <Spin />
                    </div>
                ) : null}
                {phase.kind === 'message' ? <p>{phase.message}</p> : null}
                {phase.kind === 'ready' ? (
                    <div className="table-links">
                        <p className="table-links__validity">
                            Valid from {phase.validFrom} until {phase.validUntil}.
                        </p>
                        <div className="table-links__grid">
                            <LinkColumn
                                title="Clock"
                                hint="Timekeeper phone"
                                url={phase.clockUrl}
                                qr={phase.clockQr}
                            />
                            <LinkColumn
                                title="Scoreboard"
                                hint="LED/HDMI PC"
                                url={phase.scoreboardUrl}
                                qr={phase.scoreboardQr}
                            />
                        </div>
                    </div>
                ) : null}
            </Modal>
        </>
    );
};

const LinkColumn: React.FC<{
    title: string;
    hint: string;
    url: string;
    qr?: string;
}> = ({ title, hint, url, qr }) => (
    <section className="table-links__col">
        <h3 className="table-links__title">{title}</h3>
        <p className="table-links__hint">{hint}</p>
        {qr ? <img className="table-links__qr" src={qr} alt={`${title} link QR code`} /> : null}
        <div className="table-links__url">{url}</div>
        <Button type="default" onClick={(): void => void copyLink(url)}>
            Copy link
        </Button>
    </section>
);

export default TableLinksAction;
